/**
 * Geauxjüre Reader — chapter → video deep-link map.
 *
 * Three-tier lookup against the live playlistDirectory signal:
 *   1. Verified playlist ID in directory → direct youtube.com/playlist link
 *   2. Language confirmed on Lumo channel, book not yet in directory → Lumo channel search
 *   3. Everything else → @FCBHGospelFilm channel search
 *
 * The signal starts from the bundled seed and is refreshed from the Builder
 * API on every app start (see playlist-store.ts). WatchLink re-renders
 * automatically when the signal updates — no extra wiring needed.
 */

import { playlistDirectory } from './playlist-store.js';
import { LUMO_CHANNEL_LANGUAGES } from './gospel-film-playlists.js';
import type { GospelBook } from './gospel-film-playlists.js';

export type ChapterVideo = {
  url: string;
  title: string;
  source: 'fcbh-youtube';
  /** i.ytimg.com/vi/{id}/mqdefault.jpg — null when no playlist ID is known */
  thumbnailUrl: string | null;
};

const GOSPEL_BOOKS = new Set(['MAT', 'MRK', 'LUK', 'JHN']);

const GOSPEL_FULL_NAME: Record<string, string> = {
  MAT: 'Matthew',
  MRK: 'Mark',
  LUK: 'Luke',
  JHN: 'John',
};

const GOSPEL_LAST_CHAPTER: Record<string, number> = {
  MAT: 28,
  MRK: 16,
  LUK: 24,
  JHN: 21,
};

const ISO_TO_ENGLISH: Record<string, string> = {
  afr: 'Afrikaans', aka: 'Akan', amh: 'Amharic', ara: 'Arabic',
  aze: 'Azerbaijani', bel: 'Belarusian', ben: 'Bengali', bod: 'Tibetan',
  bos: 'Bosnian', bul: 'Bulgarian', cat: 'Catalan', ces: 'Czech',
  cmn: 'Mandarin Chinese', dan: 'Danish', deu: 'German', ell: 'Greek',
  eng: 'English', epo: 'Esperanto', est: 'Estonian', eus: 'Basque',
  fas: 'Persian', fin: 'Finnish', fra: 'French', guj: 'Gujarati',
  hau: 'Hausa', heb: 'Hebrew', hin: 'Hindi', hrv: 'Croatian',
  hun: 'Hungarian', hye: 'Armenian', ibo: 'Igbo', ind: 'Indonesian',
  isl: 'Icelandic', ita: 'Italian', jpn: 'Japanese', kat: 'Georgian',
  kaz: 'Kazakh', khm: 'Khmer', kin: 'Kinyarwanda', kor: 'Korean',
  kur: 'Kurdish', lao: 'Lao', lav: 'Latvian', lit: 'Lithuanian',
  lug: 'Luganda', mal: 'Malayalam', mar: 'Marathi', mkd: 'Macedonian',
  mlg: 'Malagasy', mlt: 'Maltese', mon: 'Mongolian', msa: 'Malay',
  mya: 'Burmese', nep: 'Nepali', nld: 'Dutch', nor: 'Norwegian',
  nya: 'Chichewa', ori: 'Odia', pan: 'Punjabi', pol: 'Polish',
  por: 'Portuguese', pus: 'Pashto', ron: 'Romanian', rus: 'Russian',
  sin: 'Sinhala', slk: 'Slovak', slv: 'Slovenian', sna: 'Shona',
  som: 'Somali', spa: 'Spanish', sqi: 'Albanian', srp: 'Serbian',
  swa: 'Swahili', swe: 'Swedish', tam: 'Tamil', tel: 'Telugu',
  tgk: 'Tajik', tgl: 'Filipino', tha: 'Thai', tir: 'Tigrinya',
  tuk: 'Turkmen', tur: 'Turkish', uig: 'Uyghur', ukr: 'Ukrainian',
  urd: 'Urdu', uzb: 'Uzbek', vie: 'Vietnamese', xho: 'Xhosa',
  yor: 'Yoruba', zho: 'Chinese', zul: 'Zulu',
};

function buildVideoUrl(book: string, languageIso: string): string {
  const bookName = GOSPEL_FULL_NAME[book] ?? book;
  const langName = ISO_TO_ENGLISH[languageIso] ?? languageIso;
  const dir = playlistDirectory.value;

  // Tier 1a: verified Lumo playlist ID
  const lumoId = dir.lumo[languageIso]?.[book as GospelBook];
  if (lumoId) {
    return `https://www.youtube.com/playlist?list=${lumoId}`;
  }

  // Tier 1b: verified FCBH playlist ID
  const fcbhId = dir.fcbh[languageIso]?.[book as GospelBook];
  if (fcbhId) {
    return `https://www.youtube.com/playlist?list=${fcbhId}`;
  }

  // Tier 2: language is on Lumo channel but this book not yet in directory
  if (languageIso in dir.lumo || LUMO_CHANNEL_LANGUAGES.has(languageIso)) {
    const query = `Gospel of ${bookName} ${langName} Lumo`;
    return `https://www.youtube.com/@TheLumoProject/search?query=${encodeURIComponent(query)}`;
  }

  // Tier 3: smaller language → FCBH Gospel Film channel
  const query = `${bookName} ${langName} Gospel film`;
  return `https://www.youtube.com/@FCBHGospelFilm/search?query=${encodeURIComponent(query)}`;
}

/** Returns i.ytimg CDN URL for the first video of the matching playlist, or null. */
function buildThumbnailUrl(book: string, languageIso: string): string | null {
  const dir = playlistDirectory.value;
  const thumbnails = dir.thumbnails;

  const lumoId = dir.lumo[languageIso]?.[book as GospelBook];
  if (lumoId) {
    const vid = thumbnails[lumoId];
    if (vid) return `https://i.ytimg.com/vi/${vid}/mqdefault.jpg`;
  }
  const fcbhId = dir.fcbh[languageIso]?.[book as GospelBook];
  if (fcbhId) {
    const vid = thumbnails[fcbhId];
    if (vid) return `https://i.ytimg.com/vi/${vid}/mqdefault.jpg`;
  }
  return null;
}

/**
 * Returns a video deep-link for the given chapter, or null when no video is
 * known. Pure function — safe to call from render with no caching.
 */
export function chapterVideo(
  book: string,
  chapter: number,
  languageIso: string,
): ChapterVideo | null {
  if (!book || !Number.isInteger(chapter) || chapter < 1) return null;
  const normalizedBook = book.toUpperCase();
  if (!GOSPEL_BOOKS.has(normalizedBook)) return null;
  const max = GOSPEL_LAST_CHAPTER[normalizedBook];
  if (max == null || chapter > max) return null;

  const bookName = GOSPEL_FULL_NAME[normalizedBook];
  return {
    url: buildVideoUrl(normalizedBook, languageIso),
    title: `${bookName} ${chapter} — Gospel film`,
    source: 'fcbh-youtube',
    thumbnailUrl: buildThumbnailUrl(normalizedBook, languageIso),
  };
}
